import React, { useState, useEffect } from "react";
import { Worker } from "@react-pdf-viewer/core";
import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { Box, Typography, Container, useMediaQuery } from "@mui/material";
import PageWrapper from "../../../../shared/PageWrapper/PageWrapper";
import PageHeader from "../../../../shared/PageHeader/PageHeader";
import Button from "../../../../components/Button/Button";
import DataLoader from "../../../../shared/DataLoader/DataLoader";
import API from "../../../../api/axios";
import { getUser } from "../../../../utils/types";
import DataNotFound from "../../../../shared/DataNotFound/DataNotFound";
import { useTheme } from "@emotion/react";

const SopAndGuidelineListing = () => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<any>("");
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const user = getUser();
  const theme: any = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const roleCode =
    user?.roles?.role_code == "candidate" ||
    user?.roles?.role_code == "interviewer" ||
    user?.roles?.role_code == "administrator" ||
    user?.roles?.role_code == "bidder"
      ? user?.roles?.role_code
      : "talent_admin";

  const getManual = async () => {
    setPdfLoading(true);
    try {
      const response = await API.get(
        `/items/user_manuals?fields=*&filter[status]=published&limit=1&meta=*&filter[role_code]=${roleCode}`
      );

      const filePath = response?.data?.data[0]?.manual_file_path;
      setPdfName(response?.data?.data[0]?.manual_orgname);
      if (filePath) {
        const fileResponse = await API.post(
          "/upload/download_file",
          {
            file_path: filePath,
            file_category: "user_manual",
          },
          {
            responseType: "blob",
          }
        );
        const blob = new Blob([fileResponse.data], {
          type: response.headers["content-type"] as string,
        });

        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
      } else {
        // File path not found in the API response
      }
    } catch (error) {
      // Failed to fetch manual
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    getManual();
  }, []);

  const handleDownload = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = pdfName || "User Manual.pdf"; // Set the download filename
      link.click();
    }
  };

  return (
    <PageWrapper>
      <PageHeader title="SOP / Guidelines">
        <Button
          variant="secondary"
          variantType="contained"
          label="Download PDF"
          onClick={handleDownload}
          disabled={!pdfBlobUrl}
        />
      </PageHeader>
      <Container maxWidth="xl">
        <Box sx={{ position: "relative", marginTop: 3 }}>
          <Box
            sx={{
              border: "1px solid rgba(0, 0, 0, 0.3)",
              height: isSmallScreen
                ? "400px"
                : isMediumScreen
                ? "500px"
                : "600px",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: 3,
              backgroundColor: "white",
            }}
          >
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
              {pdfLoading ? (
                <Typography
                  variant="h6"
                  color="textSecondary"
                  sx={{
                    textAlign: "center",
                    paddingTop: 10,
                  }}
                >
                  <Box
                    height={
                      isSmallScreen
                        ? "300px"
                        : isMediumScreen
                        ? "350px"
                        : "400px"
                    }
                  >
                    <DataLoader />
                  </Box>
                </Typography>
              ) : pdfBlobUrl ? (
                <Viewer
                  fileUrl={pdfBlobUrl}
                  defaultScale={isSmallScreen ? 0.5 : isMediumScreen ? 1 : 1.5}
                />
              ) : (
                <Typography
                  variant="h6"
                  color="textSecondary"
                  sx={{
                    textAlign: "center",
                    paddingTop: 10,
                  }}
                >
                  <Box
                    height={
                      isSmallScreen
                        ? "300px"
                        : isMediumScreen
                        ? "350px"
                        : "400px"
                    }
                  >
                    <DataNotFound />
                  </Box>
                </Typography>
              )}
            </Worker>
          </Box>
        </Box>
      </Container>
    </PageWrapper>
  );
};

export default SopAndGuidelineListing;
